import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Layers3, Sparkles } from 'lucide-react';
import { useAIChat } from '@/hooks/use-ai-chat';
import { AIChatPanel } from './components/ai-chat/AIChatPanel';
import { DataSourceSelector } from './components/datasource-selector/DataSourceSelector';
import type { PipelineOption } from '@/types/ai-pipeline';
import type { NormalizedGeneratedPipeline } from '@/lib/ai-pipeline-normalizer';

export default function AIPipelineGeneratorPage() {
  const navigate = useNavigate();
  const [selectedDataSourceIds, setSelectedDataSourceIds] = useState<string[]>([]);

  const handlePipelineGenerated = useCallback(
    (pipeline: NormalizedGeneratedPipeline) => {
      navigate('/pipelines/new', {
        state: {
          generatedPipeline: pipeline,
          fromAI: true,
        },
      });
    },
    [navigate]
  );

  const {
    messages,
    sendMessage,
    isStreaming,
    isGenerating,
    generatePipeline,
    stopStreaming,
    resetChat,
  } = useAIChat({
    dataSourceIds: selectedDataSourceIds,
    onPipelineGenerated: handlePipelineGenerated,
  });

  const handleDataSourceChange = (ids: string[]) => {
    setSelectedDataSourceIds(ids);
    if (messages.length > 0) {
      resetChat();
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col bg-surface-base">
      <div className="flex items-center gap-3 border-b bg-surface-elevated px-4 py-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pipelines')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="min-w-0">
          <h1 className="font-display text-lg font-semibold">AI Pipeline Workbench</h1>
          <p className="truncate text-xs text-muted-foreground">
            Build pipelines from context-aware chat with guided output suggestions.
          </p>
        </div>
      </div>

      <div className="grid flex-1 gap-4 overflow-hidden p-4 lg:grid-cols-[320px_minmax(0,1fr)_320px]">
        <section
          data-testid="ai-workbench-context-panel"
          className="overflow-hidden rounded-xl border bg-surface-elevated"
        >
          <DataSourceSelector
            selectedIds={selectedDataSourceIds}
            onSelectionChange={handleDataSourceChange}
          />
        </section>

        <section
          data-testid="ai-workbench-chat-panel"
          className="min-h-0 overflow-hidden rounded-xl border bg-surface-elevated"
        >
          <AIChatPanel
            messages={messages}
            isStreaming={isStreaming}
            isGenerating={isGenerating}
            hasDataSources={selectedDataSourceIds.length > 0}
            onSendMessage={sendMessage}
            onStopStreaming={stopStreaming}
            onSelectPipeline={(_option: PipelineOption) => {
              // Pipeline option selected - handled by onGeneratePipeline
            }}
            onGeneratePipeline={(optionId) => generatePipeline(optionId)}
          />
        </section>

        <aside
          data-testid="ai-workbench-suggestions-panel"
          className="rounded-xl border bg-surface-elevated p-4"
        >
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Suggestions</p>
              <h2 className="font-display text-lg font-semibold tracking-tight">Generation Controls</h2>
              <p className="text-sm text-muted-foreground">
                Review context readiness and trigger generation when the prompt is stable.
              </p>
            </div>

            <div className="rounded-lg border bg-background/70 p-3">
              <p className="text-xs text-muted-foreground">Selected data sources</p>
              <p className="mt-1 text-sm font-semibold">{selectedDataSourceIds.length} selected</p>
            </div>

            <div className="space-y-2">
              <Button
                className="w-full justify-start gap-2"
                onClick={() => generatePipeline()}
                disabled={!messages.length || isGenerating || !selectedDataSourceIds.length}
              >
                <Sparkles className="h-4 w-4" />
                Generate from current chat
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => navigate('/pipelines/new')}
              >
                <Layers3 className="h-4 w-4" />
                Open manual builder
              </Button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
